import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Check, Plus, Star, Trash2, X } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Card, Field, Input, PageHead, Select, Table, Td, Textarea, Toggle } from "../ui";

const Stars = ({ value }) => (
    <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? "fill-gold text-gold" : "text-zinc-600"}`} />
        ))}
    </span>
);

const AddReview = ({ products, onDone }) => {
    const [form, setForm] = useState({
        product_slug: products[0]?.slug || "", rating: 5, title: "", body: "",
        author_name: "", author_city: "", is_verified_buyer: true, status: "approved",
    });
    const set = (patch) => setForm({ ...form, ...patch });
    const save = useMutation({
        mutationFn: async () => (await rhm.post("/reviews", form)).data,
        onSuccess: () => {
            toast.success("Review added");
            setForm({ ...form, title: "", body: "", author_name: "", author_city: "" });
            onDone();
        },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <Card title="Add a review yourself" action={
            <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="review-add-save">
                <Plus className="w-3.5 h-3.5 mr-1 inline" /> {save.isPending ? "SAVING…" : "ADD"}
            </Btn>}>
            <p className="text-[11px] text-zinc-500 mb-4">
                For reviews you collected off-site (WhatsApp, clinic, phone). Only add what a real person actually
                said — invented reviews break Indian advertising rules and Google's policy.
            </p>
            <div className="grid md:grid-cols-4 gap-3">
                <Field label="Product">
                    <Select value={form.product_slug} data-testid="review-add-product"
                        options={products.map((p) => ({ value: p.slug, label: p.name }))}
                        onChange={(e) => set({ product_slug: e.target.value })} /></Field>
                <Field label="Rating">
                    <Select value={String(form.rating)} data-testid="review-add-rating"
                        options={[5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${n} stars` }))}
                        onChange={(e) => set({ rating: Number(e.target.value) })} /></Field>
                <Field label="Customer name"><Input value={form.author_name} data-testid="review-add-name"
                    onChange={(e) => set({ author_name: e.target.value })} /></Field>
                <Field label="City"><Input value={form.author_city} data-testid="review-add-city"
                    onChange={(e) => set({ author_city: e.target.value })} /></Field>
                <Field label="Headline" className="md:col-span-2"><Input value={form.title} data-testid="review-add-title"
                    onChange={(e) => set({ title: e.target.value })} /></Field>
                <Field label="Review" className="md:col-span-2">
                    <Textarea rows={2} value={form.body} data-testid="review-add-body"
                        onChange={(e) => set({ body: e.target.value })} /></Field>
                <Toggle label="Mark as verified buyer" value={form.is_verified_buyer}
                    onChange={(v) => set({ is_verified_buyer: v })} testId="review-add-verified" />
                <Field label="Status">
                    <Select value={form.status} data-testid="review-add-status"
                        options={[{ value: "approved", label: "Live on the website" },
                                  { value: "pending", label: "Hold for later" }]}
                        onChange={(e) => set({ status: e.target.value })} /></Field>
            </div>
        </Card>
    );
};

const SettingsCard = () => {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["review-settings-crm"], queryFn: async () => (await rhm.get("/reviews/settings")).data });
    const save = useMutation({
        mutationFn: async (patch) => (await rhm.put("/reviews/settings", patch)).data,
        onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["review-settings-crm"] }); },
        onError: (e) => toast.error(apiError(e)),
    });
    if (!data) return null;

    return (
        <Card title="Who can leave a review">
            <div className="grid md:grid-cols-3 gap-4">
                <Toggle label="Verified buyers" value={data.allow_verified_buyers} testId="allow-verified"
                    onChange={(v) => save.mutate({ allow_verified_buyers: v })} />
                <Toggle label="Anyone signed in" value={data.allow_logged_in} testId="allow-logged-in"
                    onChange={(v) => save.mutate({ allow_logged_in: v })} />
                <Toggle label="Anyone, without an account" value={data.allow_anonymous} testId="allow-anonymous"
                    onChange={(v) => save.mutate({ allow_anonymous: v })} />
                <Toggle label="Approve before it goes live" value={data.require_approval} testId="require-approval"
                    onChange={(v) => save.mutate({ require_approval: v })} />
                <Toggle label="Show stars on Google automatically" value={data.auto_stars} testId="auto-stars"
                    onChange={(v) => save.mutate({ auto_stars: v })} />
                <Toggle label="Add the CRM rating into the average" value={data.blend_manual} testId="blend-manual"
                    onChange={(v) => save.mutate({ blend_manual: v })} />
                <Field label="Minimum approved reviews before stars show">
                    <Input type="number" value={data.stars_minimum} data-testid="stars-minimum"
                        onChange={(e) => save.mutate({ stars_minimum: Number(e.target.value) })} /></Field>
                <Field label="Section heading on the product page">
                    <Input value={data.section_title} data-testid="review-section-title"
                        onChange={(e) => save.mutate({ section_title: e.target.value })} /></Field>
                <Field label="Message when there are no reviews yet" className="md:col-span-2">
                    <Input value={data.empty_message} data-testid="review-empty-message"
                        onChange={(e) => save.mutate({ empty_message: e.target.value })} /></Field>
            </div>
        </Card>
    );
};

export default function RhmReviews() {
    const qc = useQueryClient();
    const [status, setStatus] = useState("pending");
    const [replyFor, setReplyFor] = useState(null);
    const [replyText, setReplyText] = useState("");

    const { data } = useQuery({
        queryKey: ["crm-reviews", status],
        queryFn: async () => (await rhm.get(`/reviews?status=${status}`)).data,
    });
    const { data: products } = useQuery({
        queryKey: ["crm-products-slim"],
        queryFn: async () => (await rhm.get("/products?limit=200")).data,
    });

    const patch = useMutation({
        mutationFn: async ({ id, body }) => (await rhm.patch(`/reviews/${id}`, body)).data,
        onSuccess: () => { toast.success("Updated"); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const remove = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/reviews/${id}`)).data,
        onSuccess: () => { toast.success("Deleted"); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["crm-reviews"] });
        qc.invalidateQueries({ queryKey: ["product-reviews"] });
    };

    const rows = data?.rows || [];
    const counts = data?.counts || {};
    const productRows = (Array.isArray(products) ? products : products?.products || [])
        .filter((p) => p.slug).map((p) => ({ slug: p.slug, name: p.name }));

    return (
        <div className="space-y-5" data-testid="rhm-reviews-page">
            <PageHead title="Customer Reviews" sub="Collect real reviews, approve them, and let Google show the stars" />

            <div className="flex flex-wrap gap-1 border-b border-white/10">
                {["pending", "approved", "rejected"].map((s) => (
                    <button key={s} onClick={() => setStatus(s)} data-testid={`review-tab-${s}`}
                        className={`px-4 py-3 mono text-[10px] tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                            status === s ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"}`}>
                        {s.toUpperCase()} {counts[s] ? `(${counts[s]})` : ""}
                    </button>
                ))}
            </div>

            <Card title={`${status[0].toUpperCase()}${status.slice(1)} reviews`}>
                <Table head={["PRODUCT", "RATING", "REVIEW", "WHO", ""]} empty="Nothing here yet"
                    testId="crm-reviews-table">
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-white/5" data-testid={`crm-review-${r.id}`}>
                            <Td className="text-sm">{r.product_name || r.product_slug}</Td>
                            <Td><Stars value={r.rating} /></Td>
                            <Td className="max-w-[380px]">
                                {r.title && <div className="text-sm">{r.title}</div>}
                                <div className="text-xs text-zinc-400 mt-1">{r.body}</div>
                                {r.reply && <div className="text-[11px] text-gold mt-2">Reply: {r.reply}</div>}
                                {replyFor === r.id && (
                                    <div className="mt-3 flex gap-2">
                                        <Input value={replyText} data-testid={`review-reply-input-${r.id}`}
                                            onChange={(e) => setReplyText(e.target.value)} placeholder="Your reply" />
                                        <Btn onClick={() => { patch.mutate({ id: r.id, body: { reply: replyText } }); setReplyFor(null); setReplyText(""); }}
                                            data-testid={`review-reply-save-${r.id}`}>SAVE</Btn>
                                    </div>
                                )}
                            </Td>
                            <Td>
                                <div className="text-sm">{r.author_name}</div>
                                <div className="mono text-[10px] text-zinc-500">
                                    {r.author_city || "—"} · {r.source === "crm" ? "added in CRM" : "website"}
                                </div>
                                {r.is_verified_buyer && (
                                    <Badge tone="good"><BadgeCheck className="w-3 h-3 mr-1 inline" />Verified</Badge>
                                )}
                            </Td>
                            <Td>
                                <div className="flex items-center gap-2">
                                    {r.status !== "approved" && (
                                        <button onClick={() => patch.mutate({ id: r.id, body: { status: "approved" } })}
                                            title="Approve" data-testid={`review-approve-${r.id}`}
                                            className="text-lime hover:brightness-125"><Check className="w-4 h-4" /></button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button onClick={() => patch.mutate({ id: r.id, body: { status: "rejected" } })}
                                            title="Reject" data-testid={`review-reject-${r.id}`}
                                            className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
                                    )}
                                    <button onClick={() => { setReplyFor(r.id); setReplyText(r.reply || ""); }}
                                        title="Reply" data-testid={`review-reply-${r.id}`}
                                        className="mono text-[10px] tracking-[0.14em] text-gold">REPLY</button>
                                    <button onClick={() => remove.mutate(r.id)} title="Delete"
                                        data-testid={`review-delete-${r.id}`}
                                        className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            {productRows.length > 0 && <AddReview products={productRows} onDone={refresh} />}
            <SettingsCard />
        </div>
    );
}
